import { unescapeReferenceToken } from '@criteria/json-pointer'
import { Operation as OperationV3_0 } from '@criteria/openapi/v3.0'
import { Operation as OperationV3_1 } from '@criteria/openapi/v3.1'
import { invariant } from '../../lib/invariant'
import { Change, OpenAPI } from './Change'

export const allMethods = ['get', 'put', 'post', 'delete', 'options', 'head', 'patch', 'trace'] as const
export type Method = (typeof allMethods)[number]

export type OperationRef = `#/paths/${string}/${string}`
export type Operation = OperationV3_0<never> | OperationV3_1<never>

export class AddOperation extends Change {
  constructor(readonly ref: OperationRef, readonly value: Operation) {
    super()
  }
  apply(document: OpenAPI): void {
    let match = this.ref.match(/^#\/paths\/(~1[^/]*)\/(get|put|post|delete|options|head|patch|trace)$/)
    if (match !== null) {
      const path = unescapeReferenceToken(match[1]) as `/${string}`
      const method = match[2] as Method

      document.paths ??= {}
      document.paths[path] ??= {}

      invariant(document.paths[path][method] === undefined, `Operation at ${this.ref} already exists`)
      document.paths[path][method] ??= this.value
    }
  }
}

export class DeleteOperation extends Change {
  constructor(readonly ref: OperationRef) {
    super()
  }
  apply(document: OpenAPI): void {
    let match = this.ref.match(/^#\/paths\/(~1[^/]*)\/(get|put|post|delete|options|head|patch|trace)$/)
    if (match !== null) {
      const path = unescapeReferenceToken(match[1]) as `/${string}`
      const method = match[2] as Method

      const pathItem = (document.paths ?? {})[path]
      if (pathItem) {
        delete pathItem[method]
      }
    }
  }
}

abstract class UpdateOperation extends Change {
  constructor(readonly ref: OperationRef) {
    super()
  }
  apply(document: OpenAPI): void {
    let match = this.ref.match(/^#\/paths\/(~1[^/]*)\/(get|put|post|delete|options|head|patch|trace)$/)
    if (match !== null) {
      const path = unescapeReferenceToken(match[1]) as `/${string}`
      const method = match[2] as Method

      const operation = ((document.paths ?? {})[path] ?? {})[method]
      if (operation) {
        this.applyToOperation(operation)
      }
    }
  }
  abstract applyToOperation(operation: Operation): void
}

export class RemoveOperationTag extends UpdateOperation {
  constructor(readonly ref: OperationRef, readonly tag: string) {
    super(ref)
  }
  applyToOperation(operation: Operation): void {
    operation.tags = operation.tags?.filter((tag) => tag !== this.tag)
  }
}

export class UpdateOperationSummary extends UpdateOperation {
  constructor(readonly ref: OperationRef, readonly value: string) {
    super(ref)
  }
  applyToOperation(operation: Operation): void {
    operation.summary = this.value
  }
}

export class UpdateOperationDescription extends UpdateOperation {
  constructor(readonly ref: OperationRef, readonly value: string) {
    super(ref)
  }
  applyToOperation(operation: Operation): void {
    operation.description = this.value
  }
}

export class UpdateOperationDeprecated extends UpdateOperation {
  constructor(readonly ref: OperationRef, readonly value: boolean) {
    super(ref)
  }
  applyToOperation(operation: Operation): void {
    operation.deprecated = this.value
  }
}

export class UpdateOperationOperationID extends UpdateOperation {
  constructor(readonly ref: OperationRef, readonly value: string) {
    super(ref)
  }
  applyToOperation(operation: Operation): void {
    operation.operationId = this.value
  }
}
